import React from 'react';
import { Button, Col, Form, Input, InputNumber, Popconfirm, Row, Select, Tag, Tooltip, Typography, Upload } from 'antd';
import {
    CaretDownOutlined,
    CaretRightOutlined,
    CloudSyncOutlined,
    DeleteOutlined,
    EditOutlined,
    GlobalOutlined,
    PauseCircleOutlined,
    PlusOutlined,
    StopOutlined,
} from '@ant-design/icons';
import { SalePropertyCardFragmentProps } from './interfaces';
import { useSalePropertyCardController } from './sale-property-card.controller';
import { PublishToMLFragment } from 'fragments/publish-to-ml/publish-to-ml.fragment';
import ModalForm from 'components/modal-form/modal-form.component';
import 'fragments/sale-property-card/sale-property-card.scss';

const MIN_PHOTOS = 4;

export const SalePropertyCardFragment: React.FC<SalePropertyCardFragmentProps> = (props) => {
    const { useController = useSalePropertyCardController } = props;
    const controller = useController(props);

    const priceLabel = `${controller.sp.currency} ${controller.sp.price.toLocaleString('es-AR')}`;
    const photoCount = controller.photos.length;
    const hasEnoughPhotos = photoCount >= MIN_PHOTOS;

    const publishDisabledReason = !props.mlConnected
        ? 'Conectá tu cuenta de MercadoLibre para publicar'
        : !hasEnoughPhotos
        ? `Necesitás al menos ${MIN_PHOTOS} fotos (tenés ${photoCount})`
        : undefined;

    return (
        <div className="sale-property-card">
            {/* Edit sale property modal */}
            <ModalForm
                title="Editar propiedad en venta"
                isvisible={controller.isEditModalVisible}
                onCancel={controller.onEditCanceled}
                onFinish={controller.onEditSubmit}
                form={controller.editForm}
                isLoading={controller.isEditLoading}
                layout="vertical"
                okButtonLabel="Guardar"
            >
                <Row gutter={12}>
                    <Col xs={24} md={12}>
                        <Form.Item label="Precio" name="price" rules={[{ required: true }]}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label="Moneda" name="currency" rules={[{ required: true }]}>
                            <Select
                                options={[
                                    { value: 'USD', label: 'USD' },
                                    { value: 'ARS', label: 'ARS' },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="Descripción (opcional)" name="description">
                            <Input.TextArea rows={3} />
                        </Form.Item>
                    </Col>
                </Row>
            </ModalForm>

            {/* Publish to ML modal */}
            <PublishToMLFragment
                isVisible={controller.isPublishModalVisible}
                salesService={props.salesService}
                propertyId={controller.sp.propertyId}
                onPublished={controller.onPublished}
                onCancel={controller.onPublishCanceled}
            />

            {/* Card header */}
            <div className="card-header" onClick={controller.onToggleExpand}>
                <div className="header-left">
                    <Typography.Text strong>
                        {controller.propertyAddress || `Propiedad #${controller.sp.propertyId}`}
                    </Typography.Text>
                    <Typography.Text type="secondary">{priceLabel}</Typography.Text>
                </div>
                <div className="header-right">
                    {controller.activeListing && (
                        <Tag color={controller.listingStatusColor}>{controller.listingStatusLabel}</Tag>
                    )}
                    {controller.isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </div>
            </div>

            {/* Card body */}
            {controller.isExpanded && (
                <div className="card-body">
                    {/* Photos */}
                    <div className="photos-section">
                        <Typography.Text
                            className={`photo-count-hint ${
                                hasEnoughPhotos ? 'ready' : photoCount === 0 ? '' : 'needs-more'
                            }`}
                        >
                            {photoCount === 0
                                ? `Sin fotos — subí al menos ${MIN_PHOTOS} para publicar en MercadoLibre`
                                : !hasEnoughPhotos
                                ? `${photoCount}/${MIN_PHOTOS} fotos — subí ${
                                      MIN_PHOTOS - photoCount
                                  } más para publicar`
                                : `${photoCount} foto${photoCount !== 1 ? 's' : ''}`}
                        </Typography.Text>
                        <div className="photos-grid">
                            {controller.photos.map((photo) => (
                                <div key={photo.id} className="photo-item">
                                    <img src={photo.fileUrl} alt="foto" />
                                    <button
                                        className="delete-photo-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            controller.onDeletePhoto(photo.id);
                                        }}
                                    >
                                        <DeleteOutlined />
                                    </button>
                                </div>
                            ))}
                            <Upload
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={(file) => {
                                    controller.onUploadPhoto(file);
                                    return false;
                                }}
                            >
                                <Tooltip title="Subir foto">
                                    <Button icon={<PlusOutlined />} loading={controller.isUploadingPhoto} />
                                </Tooltip>
                            </Upload>
                        </div>
                    </div>

                    {/* Active listing */}
                    {controller.activeListing && (
                        <div className="listing-section">
                            <Typography.Text strong>Publicación MercadoLibre</Typography.Text>
                            {controller.activeListing.externalUrl && (
                                <div style={{ marginTop: 4 }}>
                                    <a
                                        href={controller.activeListing.externalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <GlobalOutlined /> Ver publicación
                                    </a>
                                </div>
                            )}
                            <div className="listing-actions">
                                {controller.activeListing.status === 'active' && (
                                    <Popconfirm
                                        title="¿Pausar la publicación?"
                                        onConfirm={controller.onPauseListing}
                                        okText="Sí"
                                        cancelText="No"
                                    >
                                        <Button icon={<PauseCircleOutlined />} size="small">
                                            Pausar
                                        </Button>
                                    </Popconfirm>
                                )}
                                <Popconfirm
                                    title="¿Cerrar la publicación? Esta acción no se puede deshacer."
                                    onConfirm={controller.onCloseListing}
                                    okText="Sí"
                                    cancelText="No"
                                >
                                    <Button icon={<StopOutlined />} size="small" danger>
                                        Cerrar
                                    </Button>
                                </Popconfirm>
                                <Button icon={<CloudSyncOutlined />} size="small" onClick={controller.onSyncListing}>
                                    Sincronizar
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Card actions */}
                    <div className="card-actions">
                        <Button icon={<EditOutlined />} onClick={controller.onEditPressed}>
                            Editar precio
                        </Button>
                        {!controller.activeListing && (
                            <Tooltip title={publishDisabledReason}>
                                <Button
                                    type="primary"
                                    disabled={!!publishDisabledReason}
                                    onClick={controller.onPublishPressed}
                                >
                                    Publicar en MercadoLibre
                                </Button>
                            </Tooltip>
                        )}
                        <Popconfirm
                            title="¿Quitar esta propiedad de ventas?"
                            onConfirm={controller.onRemovePressed}
                            okText="Sí"
                            cancelText="No"
                        >
                            <Button danger icon={<DeleteOutlined />}>
                                Quitar de ventas
                            </Button>
                        </Popconfirm>
                    </div>
                </div>
            )}
        </div>
    );
};
